/* ORDINAMENTO LATO SERVER e PAGINAZIONE.
 *
 * Regola 6 di STATO.md: su 1.507.455 righe l'ordinamento non riordina la
 * pagina, rilancia la query. Il controllo lo DICHIARA — "nuova query sul
 * server" — mostra su quanti risultati sta lavorando e disabilita i criteri
 * mentre interroga. Un'intestazione di colonna cliccabile prometterebbe un
 * riordino istantaneo che non può mantenere.
 *
 * Le chiavi ordinabili sono quelle che il backend accetta davvero: una chiave
 * fuori lista viene ignorata in silenzio e l'utente vedrebbe un ordinamento
 * diverso da quello che ha chiesto, senza alcun messaggio.
 */

import { IconaDatabase, IconaFreccia, Pulsante } from './Base';
import { num } from '../lib/formato';
import type { ChiaveOrdinamento, VersoOrdinamento } from '../api/tipi';

export interface CriterioOrdinamento {
  chiave: ChiaveOrdinamento;
  etichetta: string;
  /** Verso naturale: il drawdown è migliore quando è piccolo. */
  versoNaturale: VersoOrdinamento;
}

/** I criteri sensati per una lista di backtest, col loro verso naturale. */
export const CRITERI_BACKTEST: CriterioOrdinamento[] = [
  { chiave: 'strategyChange', etichetta: 'Profitto', versoNaturale: 'DESC' },
  { chiave: 'drawdownMaxValue', etichetta: 'Drawdown', versoNaturale: 'ASC' },
  { chiave: 'walkForwardScore', etichetta: 'Tenuta', versoNaturale: 'DESC' },
  { chiave: 'bearYearsAvg', etichetta: 'Nei ribassi', versoNaturale: 'DESC' },
  /* L'intorno il server lo ordina, e mancava dai criteri predefiniti: è quello
     che separa una strategia da una coincidenza misurata, e una schermata che
     non se ne accorge lo lascia fuori dall'unico posto in cui si sceglie. */
  { chiave: 'intorno', etichetta: 'Intorno', versoNaturale: 'DESC' },
  { chiave: 'cagr', etichetta: 'CAGR', versoNaturale: 'DESC' },
  { chiave: 'calmar', etichetta: 'Calmar', versoNaturale: 'DESC' },
  { chiave: 'sharpe', etichetta: 'Sharpe', versoNaturale: 'DESC' },
  { chiave: 'tradesNumber', etichetta: 'Operazioni', versoNaturale: 'DESC' },
];

/** Un criterio scelto, con il suo verso: l'ordinamento e' una LISTA di questi. */
export interface OrdineScelto {
  chiave: ChiaveOrdinamento;
  verso: VersoOrdinamento;
}

export interface PropsOrdinamento {
  criteri?: CriterioOrdinamento[];
  /** I criteri attivi, in ordine di priorita': il primo decide, gli altri sciolgono le parita'. */
  ordine: OrdineScelto[];
  totale: number | null;
  inCorso: boolean;
  onCambia: (ordine: OrdineScelto[]) => void;
  /** Descrizione dell'insieme interrogato, es. "102 gruppi · 64 strategie". */
  contesto?: string;
}

/**
 * Il controllo di ordinamento, a piu' criteri.
 *
 * Su una griglia le parita' sono la norma, non l'eccezione: centinaia di configurazioni hanno lo
 * stesso rendimento al centesimo perche' producono le stesse identiche operazioni. Con un solo
 * criterio quale di quelle finisca in cima e' arbitrario, e la prima pagina diventa un caso.
 *
 * Il gesto e' quello che ci si aspetta -- Ctrl (o Cmd) mentre si preme aggiunge un criterio in
 * coda invece di sostituire -- ma NON e' l'unico modo di arrivarci: su telefono non esistono
 * tasti modificatori, e un'interfaccia che si raggiunge solo con la tastiera li' non esiste
 * proprio. Accanto a ogni criterio attivo c'e' quindi un «+» che fa la stessa cosa con un tocco,
 * e il numero d'ordine e' scritto su ciascuno.
 */
export function ControlloOrdinamento({
  criteri = CRITERI_BACKTEST,
  ordine,
  totale,
  inCorso,
  onCambia,
  contesto,
}: PropsOrdinamento) {
  const totaleTesto = totale == null ? '—' : num(totale);
  const posizione = (chiave: ChiaveOrdinamento) => ordine.findIndex((o) => o.chiave === chiave);

  function premi(c: CriterioOrdinamento, aggiungi: boolean) {
    const i = posizione(c.chiave);

    // Gia' attivo: si inverte il verso, restando dov'e' nella priorita'.
    if (i >= 0) {
      const nuovo = ordine.slice();
      nuovo[i] = { ...nuovo[i], verso: nuovo[i].verso === 'DESC' ? 'ASC' : 'DESC' };
      onCambia(nuovo);
      return;
    }

    const voce: OrdineScelto = { chiave: c.chiave, verso: c.versoNaturale };
    // Il tetto a tre non e' prudenza: oltre il terzo criterio le parita' sono gia' sciolte, e
    // ogni voce in piu' e' solo un ORDER BY che il server paga senza cambiare il risultato.
    onCambia(aggiungi ? [...ordine, voce].slice(0, 3) : [voce]);
  }

  function togli(chiave: ChiaveOrdinamento) {
    const restanti = ordine.filter((o) => o.chiave !== chiave);
    // Senza criteri non si ordina affatto: si tiene il primo della lista.
    onCambia(restanti.length ? restanti : [{ chiave: criteri[0].chiave, verso: criteri[0].versoNaturale }]);
  }

  const descrizione = ordine
    .map((o, i) => {
      const et = (criteri.find((c) => c.chiave === o.chiave)?.etichetta ?? o.chiave).toLowerCase();
      const verso = o.verso === 'DESC' ? 'dal maggiore' : 'dal minore';
      return i === 0 ? `${et} ${verso}` : `a parità, ${et} ${verso}`;
    })
    .join(' · ');

  return (
    <div className="sb-ordinamento">
      <div className="sb-ordinamento__testata">
        <IconaDatabase />
        <span className="sb-ordinamento__titolo">Ordina — nuova query sul server</span>
        <span className="sb-distintivo sb-distintivo--accento">{totaleTesto} risultati</span>
      </div>

      <div className="sb-ordinamento__criteri" role="group" aria-label="Criteri di ordinamento">
        {criteri.map((c) => {
          const i = posizione(c.chiave);
          const on = i >= 0;
          return (
            <span key={c.chiave} className="sb-criterio-gruppo">
              <button
                type="button"
                className={`sb-criterio${on ? ' sb-criterio--attivo' : ''}`}
                aria-pressed={on}
                disabled={inCorso}
                title={on ? 'Premi per invertire il verso' : 'Ctrl o Cmd per aggiungere a quelli attivi'}
                onClick={(e) => premi(c, e.ctrlKey || e.metaKey)}
              >
                {/* Il numero compare solo quando i criteri sono piu' di uno: con uno solo
                    sarebbe un «1» che non distingue niente. */}
                {on && ordine.length > 1 && <span className="sb-criterio__ordine">{i + 1}</span>}
                {c.etichetta}
                {on && <span className="sb-criterio__verso">{ordine[i].verso === 'DESC' ? '↓' : '↑'}</span>}
              </button>

              {/* La via senza tastiera: un tocco aggiunge, un tocco toglie. Su telefono e'
                  l'unica, e non e' una versione ridotta -- fa esattamente la stessa cosa. */}
              {!on && ordine.length < 3 && (
                <button
                  type="button"
                  className="sb-criterio-piu"
                  disabled={inCorso}
                  aria-label={`Aggiungi ${c.etichetta} come criterio successivo`}
                  onClick={() => premi(c, true)}
                >
                  +
                </button>
              )}
              {on && ordine.length > 1 && (
                <button
                  type="button"
                  className="sb-criterio-piu"
                  disabled={inCorso}
                  aria-label={`Togli ${c.etichetta} dai criteri`}
                  onClick={() => togli(c.chiave)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>

      <div className={`sb-ordinamento__nota${inCorso ? ' sb-ordinamento__nota--in-corso' : ''}`}>
        {inCorso
          ? 'nuova query in corso su tutti i risultati, non sulla pagina'
          : `ordinato per ${descrizione} su tutti i ${totaleTesto} risultati${contesto ? ` · ${contesto}` : ''}`}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export interface PropsPaginazione {
  pagina: number;
  pagineTotali: number;
  perPagina: number;
  totale: number | null;
  inCorso?: boolean;
  onVai: (pagina: number) => void;
}

export function Paginazione({
  pagina,
  pagineTotali,
  perPagina,
  totale,
  inCorso = false,
  onVai,
}: PropsPaginazione) {
  return (
    <div className="sb-paginazione">
      <span className="sb-paginazione__stato">
        pagina {num(pagina)} di {num(Math.max(1, pagineTotali))} · {perPagina} per pagina
        {totale != null && ` · ${num(totale)} risultati`}
      </span>
      <span className="sb-paginazione__controlli">
        <Pulsante
          variante="icona"
          aria-label="Pagina precedente"
          disabled={inCorso || pagina <= 1}
          onClick={() => onVai(pagina - 1)}
        >
          <IconaFreccia verso="sinistra" />
        </Pulsante>
        <Pulsante
          variante="icona"
          aria-label="Pagina successiva"
          disabled={inCorso || pagina >= pagineTotali}
          onClick={() => onVai(pagina + 1)}
        >
          <span style={{ transform: 'rotate(-90deg)', display: 'flex' }}>
            <IconaFreccia />
          </span>
        </Pulsante>
      </span>
    </div>
  );
}
